// =============================================================================
// RATE LIMIT POLICY — the D-2 per-route-class request budgets as DATA-IN-DB.
//
// docs/briefs/launch-week-briefs.md §D-2: "a small middleware (KV-counter,
// clock-injected via c.get('clock'), fail-OPEN on KV errors — availability
// over strictness) with per-route-class budgets as DATA (a table: auth'd
// default, expensive AI routes tighter, webhook exempt)."
//
// Mirrors nudge_policy.ts byte-for-byte in spirit: singleton-per-key rows (one
// key per route CLASS, not per route or per user), folds toward in-code
// defaults on any miss, a Source resolves it and NEVER throws. The middleware
// itself (apps/api/middleware/rateLimit.ts) reads this table ONCE per class
// per KV-cache window (see RateLimitPolicySource) rather than per-request, so
// a retune is a row UPDATE — never a deploy — without adding DB load to every
// gated request.
//
// Cross-user (NO userId) — the budget is a property of the ROUTE CLASS, not
// the caller. Per-user override is a future additive column if ever needed
// (no signal calls for it yet, matches nudge_policy's own posture).
//
// Three seeded classes (the brief's own three-tier shape):
//   'auth_default'  — any authed route not called out below. Generous: the
//                      floor that stops a wedged/looping CLIENT, not a human
//                      clicking around.
//   'ai_expensive'  — the four named routes (presence/generate, jobs/:id/tailor,
//                      discovery/companies, jobs/search) that mint a real Claude
//                      call. Tighter, because every hit costs real model spend.
//   'webhook_exempt'— present for completeness/documentation (Stripe's webhook
//                      is unauthenticated + has no userId to key a per-user
//                      counter against); the middleware itself never mounts on
//                      the webhook route, so this row is never actually
//                      resolved in the current wiring — kept as an explicit,
//                      readable record of the exemption rather than a silent
//                      omission (a future per-IP webhook budget would read it).
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { boolean, index, integer, pgTable, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The route-class keys this brief seeds. A future class is just a new row —
 *  no shape change — so this union is documentation, not an enum constraint. */
export type RateLimitRouteClass = 'auth_default' | 'ai_expensive' | 'webhook_exempt';

export const rateLimitPolicy = pgTable(
  'rate_limit_policy',
  {
    id: id(),
    /** The route-class key — 'auth_default' | 'ai_expensive' | 'webhook_exempt'
     *  today. UNIQUE so a re-seed/recalibration upserts in place (ON CONFLICT)
     *  instead of duplicating. */
    routeClass: varchar('route_class', { length: 32 }).notNull(),
    /** Rolling-window length, in seconds, the counter measures over. */
    windowSeconds: integer('window_seconds').notNull(),
    /** Max requests permitted per (userId, routeClass) inside one window. At or
     *  above this count the middleware serves 429 rather than incrementing further. */
    maxRequests: integer('max_requests').notNull(),
    /** Row toggle — an inactive row is treated as absent, never silently applied
     *  (matches nudge_policy.active's exact posture). */
    active: boolean('active').notNull().default(true),
    ...timestamps,
  },
  (t) => ({
    routeClassIdx: index('rate_limit_policy_route_class_idx').on(t.routeClass),
    routeClassUq: uniqueIndex('rate_limit_policy_route_class_uq').on(t.routeClass),
  }),
);
